import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReduxDispatch, useReduxSelector } from '../redux/hooks';
import { toast } from 'sonner';
import { CircularProgress } from '@mui/material';
import { UserAPI } from '../middleware';

const PaymentCallback = () => {
    const navigate = useNavigate();
    const dispatch = useReduxDispatch();
    const [isProcessing, setIsProcessing] = useState(true);
    const signupData = useReduxSelector((state) => state.signup.user);

    const isFromMobileApp = () => {
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        if (!isMobile) return false;

        const urlParams = new URLSearchParams(window.location.search);
        const qs = urlParams.get('qs');
        return qs !== null;
    };

    const handlePaymentCompletion = async (status: string, paymentData: any) => {
        setIsProcessing(false);

        setTimeout(async () => {
            if (isFromMobileApp()) {
                const urlParams = new URLSearchParams(window.location.search);
                const signupData = urlParams.get('signup_data');
                const contactValue = urlParams.get('contact');

                const callbackData = {
                    status,
                    reference: paymentData.aapf_txn_ref,
                    amount: paymentData.aapf_txn_amt,
                    currency: paymentData.aapf_txn_currency,
                    transactionId: paymentData.aapf_txn_cref,
                    paymentScheme: paymentData.aapf_txn_payScheme,
                    message: paymentData.aapf_txn_sc_msg,
                    signupData: signupData || '',
                    contact: contactValue || ''
                };

                window.location.href = `trusthospital://payment-callback?data=${encodeURIComponent(JSON.stringify(callbackData))}`;
            } else {
                if (window.opener) {
                    if (paymentData.aapf_txn_ref) {
                        window.opener.location.href = `/otp-input?txn_ref=${encodeURIComponent(paymentData.aapf_txn_ref)}&client_ref=${encodeURIComponent(paymentData.aapf_txn_cref || '')}`;
                    } else {
                        window.opener.location.href = '/otp-input';
                    }
                    window.close();
                } else {

                    const existingSignupData = signupData?.new_signup || JSON.parse(localStorage.getItem('signUpData') || 'null');

                    if (!existingSignupData) {
                        toast.error('Unable to proceed: missing signup data');
                        navigate('/newsignup');
                        return;
                    }


                    let contactNumber = '';
                    if (existingSignupData.data && Array.isArray(existingSignupData.data) && existingSignupData.data.length > 0) {
                        const firstData = existingSignupData.data[0];
                        if (firstData.patientContacts && firstData.patientContacts.length > 0) {
                            // Find mobile contact
                            const mobileContact = firstData.patientContacts.find((contact: any) =>
                                contact.type === 'mobile' || contact.type?.value === 'mobile'
                            );
                            if (mobileContact) {
                                contactNumber = mobileContact.value;
                            }
                        }


                        if (firstData.signUpToken) {
                            localStorage.setItem('signUpToken', firstData.signUpToken);
                        }


                        if (firstData.user) {
                            localStorage.setItem('userId', firstData.user.id);
                            localStorage.setItem('username', firstData.user.username);
                        }
                    }


                    // Check if we have a valid contact number
                    if (!contactNumber) {
                        toast.error('Unable to proceed: Contact number not found in signup data');
                        navigate('/newsignup');
                        return;
                    }

                    const txnRef = paymentData.aapf_txn_ref;
                    const clientRef = paymentData.aapf_txn_cref;


                    localStorage.setItem('signUpData', JSON.stringify(existingSignupData));
                    if (txnRef) localStorage.setItem('txnRef', txnRef);
                    if (clientRef) localStorage.setItem('clientRef', clientRef);
                    // Don't store masked phone number
                    localStorage.setItem('patientContact', contactNumber);


                    navigate('/otp-input', {
                        state: {
                            signUpResponseData: existingSignupData,
                            patientContact: contactNumber,
                            txnRef: txnRef,
                            clientRef: clientRef
                        }
                    });


                    if (txnRef) {
                        window.history.replaceState(
                            null,
                            '',
                            `/otp-input?txn_ref=${encodeURIComponent(txnRef)}&client_ref=${encodeURIComponent(clientRef || '')}`
                        );
                    }
                }
            }
        }, 2000);
    };

    useEffect(() => {
        const fetchPaymentStatus = async () => {
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const qs = urlParams.get('qs');
                if (!qs) throw new Error('No payment data found');

                const paymentData = JSON.parse(decodeURIComponent(qs));
                console.log('Payment Data:', paymentData);

                if (!paymentData.aapf_txn_ref || !paymentData.aapf_txn_sc) {
                    throw new Error('Invalid payment data structure');
                }


                const isSuccessful = paymentData.aapf_txn_sc === "0";

                if (isSuccessful) {
                    toast.success('Payment successful!');
                    handlePaymentCompletion('success', paymentData);
                } else {
                    toast.error(`Payment failed: ${paymentData.aapf_txn_sc_msg}`);
                    handlePaymentCompletion('failed', paymentData);
                }
            } catch (error) {
                console.error('Error processing payment:', error);
                toast.error('An error occurred while processing your payment.');
                handlePaymentCompletion('error', {});
            }
        };

        fetchPaymentStatus();
    }, [navigate, dispatch]);

    if (!isProcessing) {
        return null;
    }

    return (
        <div className="flex justify-center items-center h-screen flex-col bg-white">
            <CircularProgress size={60} style={{ color: '#FF6B00' }} />
            <h1 className="text-xl mt-4 text-gray-800 font-semibold">Processing your payment...</h1>
            <p className="text-sm mt-2 text-gray-600">Please wait while we confirm your payment</p>
        </div>
    );
};

export default PaymentCallback;